import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { message } from "antd";
import apiClient from "utils/apiClient";
import tokenRefreshService from "utils/tokenRefreshService";
import AuthLoading from "components/common/AuthLoading";
import paths from "routes/paths";
import { ensureClientId } from "utils/clientBootstrap";
import { O_API_KEY, O_CLIENT_APP, IDENTITY_SERVICE_BASE_URL } from "constants";
import { normalizeIsoExpiry } from "utils/normalizeIsoExpiry";
import { useMultiTabAuth } from "hooks/auth/useMultiTabAuth";
import {
  clearPostLoginRedirect,
  getCurrentAppPath,
  savePostLoginRedirect,
} from "utils/postLoginRedirect";
import {
  assertAppleRedirectUri,
  getAppleRedirectUri,
} from "utils/appleAuth";

const APPLE_REDIRECT_URI = assertAppleRedirectUri(getAppleRedirectUri());

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") {
    return {};
  }

  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) {
      return {};
    }

    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
};

const getAppleIdentityToken = (appleResponse = {}) =>
  appleResponse?.authorization?.id_token ||
  appleResponse?.authorization?.idToken ||
  appleResponse?.identityToken ||
  appleResponse?.id_token ||
  appleResponse?.idToken ||
  null;

const getAppleAuthorizationCode = (appleResponse = {}) =>
  appleResponse?.authorization?.code ||
  appleResponse?.authorizationCode ||
  appleResponse?.code ||
  null;

const getAppleUserId = (appleResponse = {}, jwtPayload = {}) =>
  (typeof appleResponse?.user === "string" ? appleResponse.user : "") ||
  appleResponse?.userId ||
  jwtPayload?.sub ||
  "";

const getApplePersonName = (appleResponse = {}, jwtPayload = {}) => {
  const appleUser = typeof appleResponse?.user === "object" && appleResponse.user ? appleResponse.user : {};
  const firstName =
    appleUser?.name?.firstName ||
    appleUser?.name?.givenName ||
    jwtPayload?.given_name ||
    appleResponse?.firstName ||
    "";
  const lastName =
    appleUser?.name?.lastName ||
    appleUser?.name?.familyName ||
    jwtPayload?.family_name ||
    appleResponse?.lastName ||
    "";

  return { firstName, lastName };
};

const getAppleEmail = (appleResponse = {}, jwtPayload = {}) =>
  appleResponse?.user?.email ||
  appleResponse?.email ||
  jwtPayload?.email ||
  "";

const getApplePhone = (appleResponse = {}, jwtPayload = {}) =>
  appleResponse?.phone ||
  jwtPayload?.phone_number ||
  "";

const persistAuthSession = ({
  accessToken,
  refreshToken,
  accessTokenExpiresAt,
  refreshTokenExpiresAt,
  privileges,
}) => {
  sessionStorage.setItem("authToken", accessToken);
  sessionStorage.setItem("refreshToken", refreshToken || "");
  sessionStorage.setItem("accessTokenExpiresAt", accessTokenExpiresAt || "");
  sessionStorage.setItem("refreshTokenExpiresAt", refreshTokenExpiresAt || "");
  sessionStorage.setItem("userPrivileges", JSON.stringify(privileges || []));
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialCheck, setIsInitialCheck] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [accessTokenExpiresAt, setAccessTokenExpiresAt] = useState(null);
  const [refreshTokenExpiresAt, setRefreshTokenExpiresAt] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [userPrivileges, setUserPrivileges] = useState([]);
  const logoutRef = useRef(() => {});

  const { isLeader, requestSession, broadcastLogout, broadcastTokenUpdate } =
    useMultiTabAuth({
      onSessionReceived: () => {
        checkAuthStatus();
      },
      onLogoutReceived: () => {
        logoutRef.current(true);
      },
      onTokensUpdated: () => {
        // Token refresh should stay silent and must not disrupt in-progress UI.
        // We persist refreshed tokens in storage and let request interceptors read from there.
      },
    });

  const logout = useCallback(
    async (isRemote = false, redirectPath = paths.simpleLogin) => {
      // Cleanup token refresh service
      tokenRefreshService.cleanup();

      if (redirectPath === paths.simpleSessionExpired) {
        savePostLoginRedirect(getCurrentAppPath());
      } else {
        clearPostLoginRedirect();
      }

      // Clear all auth state
      setUser(null);
      setIsAuthenticated(false);
      setAuthToken(null);
      setRefreshToken(null);
      setAccessTokenExpiresAt(null);
      setRefreshTokenExpiresAt(null);
      setUserPrivileges([]);
      // Clear storage
      sessionStorage.clear();

      // only broadcast if this tab initiated logout
      if (!isRemote) {
        message.success("Logged out successfully");
        broadcastLogout();
      }
      window.location.href = redirectPath;
      return true;
    },
    [broadcastLogout],
  );

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  const bootstrapClient = async () => {
    try {
      const id = await ensureClientId();
      setClientId(id);
      return true;
    } catch (error) {
      console.error("Client bootstrap failed:", error);
      return false;
    }
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      const storedToken = sessionStorage.getItem("authToken");
      const storedRefreshToken = sessionStorage.getItem("refreshToken");
      const storedAccessTokenExpiresAt = sessionStorage.getItem(
        "accessTokenExpiresAt",
      );
      const storedRefreshTokenExpiresAt = sessionStorage.getItem(
        "refreshTokenExpiresAt",
      );
      const storedPrivileges = JSON.parse(
        sessionStorage.getItem("userPrivileges") || "[]",
      );

      if (!storedToken) {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        setIsInitialCheck(false);
        return null;
      }

      // Update state with stored values
      setAuthToken(storedToken);
      setRefreshToken(storedRefreshToken);
      setAccessTokenExpiresAt(normalizeIsoExpiry(storedAccessTokenExpiresAt));
      setRefreshTokenExpiresAt(storedRefreshTokenExpiresAt);
      setUserPrivileges(storedPrivileges);

      // Verify token and get basic identity
      const identityRes = await apiClient.get(
        "/identity-service/api/v1/users/me",
        {
          // headers: { Authorization: `Bearer ${storedToken}` },
        },
      );

      if (identityRes.data && identityRes.data.id) {
        // Fetch FULL profile after identity confirmed
        const profileRes = await apiClient.get(
          "/profile-service/api/v1/user-profiles/get?refresh=true",
          {
            headers: { Authorization: `Bearer ${storedToken}` },
          },
        );

        let userData = null;

        if (profileRes.data && profileRes.data.id) {
          userData = profileRes.data;
          setUser(userData);
          setUserPrivileges(storedPrivileges);
          setIsAuthenticated(true);
        } else {
          // Profile fetch failed, fallback to identity
          userData = identityRes.data;
          setUser(userData);
          setIsAuthenticated(true);
        }

        return userData;
      } else {
        // Token invalid or identity not found
        sessionStorage.removeItem("authToken");
        setUser(null);
        setIsAuthenticated(false);
        return null;
      }
    } catch (error) {
      console.error("Auth check failed:", error);

      // Handle 401 errors
      if (error.response?.status === 401) {
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("accessTokenExpiresAt");
        sessionStorage.removeItem("refreshTokenExpiresAt");
        setAuthToken(null);
      }

      setUser(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setLoading(false);
      setIsInitialCheck(false);
    }
  }, []);

  // Initialize authentication on mount
  useEffect(() => {
    const initialize = async () => {
      // Step 1: Bootstrap client
      const clientReady = await bootstrapClient();
      if (!clientReady) {
        setLoading(false);
        setIsInitialCheck(false);
        return;
      }

      // Step 2: Check auth status
      await requestSession();
      await checkAuthStatus();
    };

    initialize();
  }, [checkAuthStatus]);

  // Initialize token refresh service when authenticated
  useEffect(() => {
    if (isAuthenticated && refreshToken && accessTokenExpiresAt && isLeader) {
      // Callback for when tokens are refreshed
      const handleTokenRefreshed = (tokenData) => {
        // Update session storage
        if (tokenData.accessToken) {
          sessionStorage.setItem("authToken", tokenData.accessToken);
        }
        if (tokenData.refreshToken) {
          sessionStorage.setItem("refreshToken", tokenData.refreshToken);
        }
        if (tokenData.accessTokenExpiresAt) {
          const normalizedExpiry = normalizeIsoExpiry(
            tokenData.accessTokenExpiresAt,
          );
          sessionStorage.setItem(
            "accessTokenExpiresAt",
            normalizedExpiry || "",
          );
        }
        if (tokenData.refreshTokenExpiresAt) {
          sessionStorage.setItem(
            "refreshTokenExpiresAt",
            tokenData.refreshTokenExpiresAt,
          );
        }

        broadcastTokenUpdate({
          authToken: tokenData.accessToken,
          refreshToken: tokenData.refreshToken,
          accessTokenExpiresAt: tokenData.accessTokenExpiresAt,
          refreshTokenExpiresAt: tokenData.refreshTokenExpiresAt,
          userPrivileges: sessionStorage.getItem("userPrivileges"),
        });
      };

      // Callback for when session expires
      const handleSessionExpired = () => {
        logout(false, paths.simpleSessionExpired);
      };

      // Initialize the service
      tokenRefreshService.initialize(
        handleSessionExpired,
        handleTokenRefreshed,
      );
    } else {
      // Cleanup when not authenticated
      tokenRefreshService.cleanup();
    }

    return () => {
      tokenRefreshService.cleanup();
    };
  }, [
    isAuthenticated,
    refreshToken,
    accessTokenExpiresAt,
    isLeader,
    logout,
    broadcastTokenUpdate,
  ]);

  const login = async (email, password) => {
    try {
      const { data } = await apiClient.post(
        "/identity-service/api/v1/auth/login",
        {
          email,
          password,
        },
        {
          headers: {
            "O-API-KEY": O_API_KEY,
            "O-CLIENT-APP": O_CLIENT_APP,
          },
        },
      );

      if (!data.accessToken) {
        throw new Error("No access token returned");
      }

      const {
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        clientId,
        privileges,
      } = data;

      // Store all token data in sessionStorage
      sessionStorage.setItem("authToken", accessToken);
      sessionStorage.setItem("refreshToken", refreshToken || "");
      sessionStorage.setItem(
        "accessTokenExpiresAt",
        accessTokenExpiresAt || "",
      );
      sessionStorage.setItem(
        "refreshTokenExpiresAt",
        refreshTokenExpiresAt || "",
      );
      sessionStorage.setItem(
        "userPrivileges",
        JSON.stringify(privileges || []),
      );

      // Update state
      setAuthToken(accessToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      // Check auth status to fetch user profile
      const userData = await checkAuthStatus();

      message.success("Signed in successfully");

      return {
        success: true,
        data: userData || { email },
        user: userData,
      };
    } catch (error) {
      console.error("Login failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const url = `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/google`;
      const { data } = await apiClient.post(url, { idToken }, {
        headers: {
          "O-API-KEY": O_API_KEY,
          "O-CLIENT-APP": O_CLIENT_APP,
        },
      });

      if (!data.accessToken) {
        throw new Error("No access token returned from Google login");
      }

      const {
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        clientId,
        privileges,
      } = data;

      sessionStorage.setItem("authToken", accessToken);
      sessionStorage.setItem("refreshToken", refreshToken || "");
      sessionStorage.setItem("accessTokenExpiresAt", accessTokenExpiresAt || "");
      sessionStorage.setItem("refreshTokenExpiresAt", refreshTokenExpiresAt || "");
      sessionStorage.setItem("userPrivileges", JSON.stringify(privileges || []));

      setAuthToken(accessToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();

      message.success("Google sign-in successful");

      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      console.error("Google login failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const loginWithGoogleAdmin = async (idToken) => {
    try {
      const url = `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/google/admin`;
      const { data } = await apiClient.post(url, { idToken }, {
        headers: {
          "O-API-KEY": O_API_KEY,
          "O-CLIENT-APP": O_CLIENT_APP,
        },
      });

      if (!data.accessToken) {
        throw new Error("No access token returned from Google admin login");
      }

      const {
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        clientId,
        privileges,
      } = data;

      sessionStorage.setItem("authToken", accessToken);
      sessionStorage.setItem("refreshToken", refreshToken || "");
      sessionStorage.setItem("accessTokenExpiresAt", accessTokenExpiresAt || "");
      sessionStorage.setItem("refreshTokenExpiresAt", refreshTokenExpiresAt || "");
      sessionStorage.setItem("userPrivileges", JSON.stringify(privileges || []));

      setAuthToken(accessToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();

      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      console.error("Google admin login failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const loginWithGoogleCode = async (code) => {
    try {
      const url = `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/google/code`;
      const { data } = await apiClient.post(url, { code }, {
        headers: {
          "O-API-KEY": O_API_KEY,
          "O-CLIENT-APP": O_CLIENT_APP,
        },
      });

      if (!data.accessToken) {
        throw new Error("No access token returned from Google code exchange");
      }

      const {
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        clientId,
        privileges,
      } = data;

      sessionStorage.setItem("authToken", accessToken);
      sessionStorage.setItem("refreshToken", refreshToken || "");
      sessionStorage.setItem("accessTokenExpiresAt", accessTokenExpiresAt || "");
      sessionStorage.setItem("refreshTokenExpiresAt", refreshTokenExpiresAt || "");
      sessionStorage.setItem("userPrivileges", JSON.stringify(privileges || []));

      setAuthToken(accessToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();

      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      console.error("Google code login failed:", error);
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const loginWithFacebook = async (accessToken, password = null) => {
    try {
      const url = `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/facebook`;
      const { data } = await apiClient.post(
        url,
        {
          accessToken,
          ...(password ? { password } : {}),
        },
        {
          headers: {
            "O-API-KEY": O_API_KEY,
            "O-CLIENT-APP": O_CLIENT_APP,
          },
        },
      );

      if (data?.requiresPassword) {
        return {
          success: false,
          details: data,
          error: data?.message || "Facebook sign-up requires password setup.",
        };
      }

      const backendToken = data?.accessToken || data?.token;
      if (!backendToken) {
        throw new Error("No access token returned from Facebook sign-up");
      }

      const {
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      } = data;

      persistAuthSession({
        accessToken: backendToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      });
      setAuthToken(backendToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();

      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      console.error("Facebook sign-up failed:", error);
      return {
        success: false,
        details: error.response?.data || null,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const loginWithSocialProvider = async (provider, payload = {}) => {
    try {
      const normalizedProvider = String(provider || "").trim().toLowerCase();
      if (!normalizedProvider) {
        throw new Error("Social provider is required");
      }

      const url = `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/${normalizedProvider}`;
      const { data, status } = await apiClient.post(url, payload, {
        headers: {
          "O-API-KEY": O_API_KEY,
          "O-CLIENT-APP": O_CLIENT_APP,
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const backendToken = data?.accessToken || data?.token || data?.access_token || data?.jwt;
      const needsMoreDetails =
        data?.requiresPassword ||
        data?.requiresLinkApproval ||
        data?.userInfo ||
        (status === 201 && !backendToken);

      if (needsMoreDetails && !backendToken) {
        return {
          success: false,
          status,
          details: data,
          error:
            data?.message ||
            "Social sign-up requires a few more account details.",
        };
      }

      if (!backendToken) {
        throw new Error("No access token returned from social sign-up");
      }

      const {
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      } = data;

      persistAuthSession({
        accessToken: backendToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      });
      setAuthToken(backendToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();

      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      console.error(`${provider} social sign-up failed:`, error);
      return {
        success: false,
        status: error.response?.status,
        details: error.response?.data || null,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const completeSocialSession = useCallback(async ({
    accessToken,
    refreshToken,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
    privileges,
  } = {}) => {
    try {
      if (!accessToken) {
        throw new Error("No access token returned from social sign-in");
      }

      persistAuthSession({
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      });
      setAuthToken(accessToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();
      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || "Unable to complete social sign-in.",
      };
    }
  }, [checkAuthStatus]);

  const exchangeAppleLogin = async ({
    appleResponse = null,
    code = null,
    idToken = null,
    password = null,
  } = {}) => {
    try {
      const url = `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/apple`;
      const source = appleResponse || {
        authorization: {
          code,
          id_token: idToken,
        },
        code,
        idToken,
        identityToken: idToken,
      };
      const identityToken = getAppleIdentityToken(source);
      const authorizationCode = getAppleAuthorizationCode(source);
      const jwtPayload = identityToken ? decodeJwtPayload(identityToken) : {};
      const { firstName, lastName } = getApplePersonName(source, jwtPayload);
      const email = getAppleEmail(source, jwtPayload);
      const phone = getApplePhone(source, jwtPayload);
      const appleUserId = getAppleUserId(source, jwtPayload);

      const payload = {
        identityToken,
        authorizationCode,
        user: appleUserId,
        fullName:
          firstName || lastName
            ? {
                givenName: firstName || "",
                familyName: lastName || "",
              }
            : undefined,
        email,
        firstName,
        lastName,
        phone,
        redirectUri: APPLE_REDIRECT_URI,
        ...(password ? { password } : {}),
      };

      const { data } = await apiClient.post(url, payload, {
        headers: {
          "O-API-KEY": O_API_KEY,
          "O-CLIENT-APP": O_CLIENT_APP,
        },
      });

      if (data?.requiresPassword || data?.requiresLinkApproval) {
        return {
          success: false,
          ...data,
          details: data,
          error:
            data?.message ||
            "Apple sign-in requires additional account setup.",
        };
      }

      if (!data.accessToken) {
        throw new Error("No access token returned from Apple sign-in");
      }

      const {
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      } = data;

      persistAuthSession({
        accessToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      });
      setAuthToken(accessToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();

      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      console.error("Apple sign-in failed:", error);
      return {
        success: false,
        details: error.response?.data || null,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const loginWithApple = async (appleResponse, password = null) =>
    exchangeAppleLogin({ appleResponse, password });

  const loginWithAppleCode = async (appleResponse) =>
    exchangeAppleLogin({ appleResponse });

  const linkSocialAccount = async (provider = "apple", {
    email,
    appleUserId,
    providerUserId,
    appleResponse = null,
    identityToken,
    authPayload = {},
    password,
    verificationCode,
    linkingProcessId,
  } = {}) => {
    try {
      const normalizedProvider = String(provider || "apple").trim().toLowerCase();
      const url = `${IDENTITY_SERVICE_BASE_URL}/api/v1/auth/account/link`;
      const source = appleResponse || {
        authorization: {
          id_token: identityToken,
        },
        identityToken,
        user: appleUserId || providerUserId,
      };
      const resolvedIdentityToken =
        identityToken || authPayload?.identityToken || authPayload?.idToken || getAppleIdentityToken(source);
      const resolvedAppleUserId =
        appleUserId || providerUserId || getAppleUserId(source, decodeJwtPayload(resolvedIdentityToken));
      const decodedProviderToken = decodeJwtPayload(
        authPayload?.idToken || authPayload?.identityToken || resolvedIdentityToken,
      );
      const resolvedProviderUserId =
        providerUserId ||
        authPayload?.providerUserId ||
        authPayload?.userId ||
        authPayload?.googleUserId ||
        authPayload?.appleUserId ||
        authPayload?.linkedinUserId ||
        authPayload?.user ||
        (normalizedProvider === "apple" ? resolvedAppleUserId : "") ||
        decodedProviderToken?.sub ||
        decodedProviderToken?.oid ||
        decodedProviderToken?.id ||
        "";
      const { data, status } = await apiClient.post(
        url,
        {
          provider: normalizedProvider.toUpperCase(),
          email,
          ...(resolvedProviderUserId ? { providerUserId: resolvedProviderUserId } : {}),
          ...(authPayload?.tenantId ? { tenantId: authPayload.tenantId } : {}),
          ...(normalizedProvider === "apple" && resolvedIdentityToken ? { identityToken: resolvedIdentityToken } : {}),
          ...(password ? { password } : {}),
          ...(verificationCode ? { verificationCode } : {}),
          ...(linkingProcessId ? { linkingProcessId } : {}),
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "O-API-KEY": O_API_KEY,
            "O-CLIENT-APP": O_CLIENT_APP,
          },
          validateStatus: () => true,
        },
      );

      if (data?.requiresVerificationCode) {
        return {
          success: false,
          requiresVerificationCode: true,
          data,
          verificationCode: data?.verificationCode,
          linkingProcessId: data?.linkingProcessId,
          error: data.message || "Verification code required",
        };
      }

      if (
        data?.requiresPassword ||
        data?.requiresLinkApproval ||
        (status === 401 && !password && String(data?.message || data?.error || "").toLowerCase().match(/password|invalid/))
      ) {
        return {
          success: false,
          requiresPassword: data?.requiresPassword || status === 401,
          requiresLinkApproval: data?.requiresLinkApproval,
          details: data,
          data,
          error:
            data?.message ||
            "Account linking requires additional setup.",
        };
      }

      if (status < 200 || status >= 300 || data?.raw) {
        return {
          success: false,
          data,
          error: data?.message || data?.error || `Server error ${status}`,
        };
      }

      const backendToken = data?.accessToken || data?.token || data?.access_token || data?.jwt;
      if (!backendToken) {
        return {
          success: false,
          data,
          error: data?.message || "No access token returned from account linking",
        };
      }

      const {
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      } = data;

      persistAuthSession({
        accessToken: backendToken,
        refreshToken,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
        privileges,
      });
      setAuthToken(backendToken);
      setRefreshToken(refreshToken || null);
      setAccessTokenExpiresAt(normalizeIsoExpiry(accessTokenExpiresAt) || null);
      setRefreshTokenExpiresAt(refreshTokenExpiresAt || null);
      setUserPrivileges(privileges || []);

      const userData = await checkAuthStatus();

      return {
        success: true,
        data: userData,
        user: userData,
      };
    } catch (error) {
      console.error(`${provider} account linking failed:`, error);
      return {
        success: false,
        details: error.response?.data || null,
        error: error.response?.data?.message || error.message,
      };
    }
  };

  const linkAppleAccount = (payload = {}) => linkSocialAccount("apple", payload);

  const isLoggedIn = () => !!(isAuthenticated && user);

  const refreshAuth = async () => {
    try {
      await checkAuthStatus();
    } catch (error) {
      console.error("Failed to refresh authentication:", error);
    }
};

  const value = {
    user,
    isAuthenticated,
    loading,
    isInitialCheck,
    login,
    loginWithGoogle,
    loginWithGoogleCode,
    loginWithGoogleAdmin,
    loginWithFacebook,
    loginWithSocialProvider,
    completeSocialSession,
    loginWithApple,
    loginWithAppleCode,
    linkSocialAccount,
    linkAppleAccount,
    logout,
    checkAuthStatus,
    isLoggedIn,
    refreshAuth,
    privileges: userPrivileges,
    clientId,
    authToken,
    refreshToken,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <AuthLoading /> : children}
    </AuthContext.Provider>
  );
};

// Export only ONE default export
export default AuthProvider;
